import {
  PIECE_VALUE_NAMES,
  PIECE_VALUES,
  type GameBoard,
  type GamePiece,
  type RawPiece,
} from '@blind-kings/game-core';
import { useMemo } from 'react';
import { BoardCanvas } from '../Game/pixi';

/** Default demo board width in pixels. */
export const RULE_DEMO_BOARD_SIZE = 240;

export type RuleDemoBoardProps = {
  gameBoard: GameBoard;
  seedPiece?: GamePiece | RawPiece;
  prompt?: string;
  /** Board width in pixels. Height follows aspectRatio. */
  size?: number;
  /** height / width. Use 1 for square demos. */
  aspectRatio?: number;
};

function toRawBoard(gameBoard: GameBoard): RawPiece[][] {
  return (gameBoard.board as GamePiece[][]).map((row) =>
    row.map((piece) => ({
      id: piece.id,
      value: piece.value,
      teamIndex: piece.teamIndex,
    }))
  );
}

function resolveSeed(
  gameBoard: GameBoard,
  seedPiece?: GamePiece | RawPiece
): GamePiece | undefined {
  if (!seedPiece?.id && seedPiece?.id !== 0) return undefined;
  return gameBoard.gamePiecesById[seedPiece.id!];
}

export default function RuleDemoBoard({
  gameBoard,
  seedPiece,
  prompt,
  size = RULE_DEMO_BOARD_SIZE,
  aspectRatio = 1,
}: RuleDemoBoardProps) {
  const selectedPiece = useMemo(
    () => resolveSeed(gameBoard, seedPiece),
    [gameBoard, seedPiece]
  );
  const rawBoard = useMemo(() => toRawBoard(gameBoard), [gameBoard]);
  const actionablePieces = selectedPiece?.allAvailablePiecesForAction ?? [];

  const statusLabel = useMemo(() => {
    if (!selectedPiece || selectedPiece.isEmpty) return null;
    if (selectedPiece.value === PIECE_VALUES.Unturned) {
      return 'Face-down piece';
    }
    const name = PIECE_VALUE_NAMES[selectedPiece.value] ?? 'Piece';
    return `${name} · CP ${selectedPiece.value}`;
  }, [selectedPiece]);

  const height = Math.round(size * aspectRatio);

  return (
    <div className="flex w-fit max-w-full flex-col gap-2">
      <div
        className="pointer-events-none border border-bk-gold-dim/40 bg-bk-bg/40 p-1.5"
        style={{ width: size, height }}
      >
        <div className="h-full w-full border-2 border-black">
          <BoardCanvas
            board={rawBoard}
            gameBoard={gameBoard}
            interactive={false}
            displayPieceValues
            selectedPiece={selectedPiece}
            actionablePieces={actionablePieces}
          />
        </div>
      </div>
      {(prompt || statusLabel) && (
        <div className="w-full" style={{ maxWidth: size }}>
          {prompt ? (
            <p className="text-xs text-bk-muted sm:text-sm">{prompt}</p>
          ) : null}
          {statusLabel ? (
            <p className="mt-0.5 text-xs font-semibold text-bk-gold sm:text-sm">
              {statusLabel}
            </p>
          ) : null}
        </div>
      )}
    </div>
  );
}
